import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { Toaster } from 'sonner';
import { MotionConfig } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import TofBoxShare from './utils/tofboxSharePlugin';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { VaultProvider } from './contexts/VaultContext';
import ConsentBanner from './components/ads/ConsentBanner';
import UpdateBanner from './components/pwa/UpdateBanner';
import { initPosthogConsent } from './utils/posthogConsent';
import { initAwinConsent } from './utils/awinConsent';
import { sharedLinkPage } from './utils/publicOrigin';
import { SEO_PAGE_ROUTES } from './content/seoPagePaths';
import { pendingInvite, rememberAfterSignup } from './utils/afterSignup';
import i18n from './i18n';
import './App.css';

const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const ParkingSharePage = lazy(() => import('./pages/public/ParkingSharePage'));
const TofMeetSharePage = lazy(() => import('./pages/public/TofMeetSharePage'));
const ChoreWheelSharePage = lazy(() => import('./pages/public/ChoreWheelSharePage'));
const CountdownSharePage = lazy(() => import('./pages/public/CountdownSharePage'));
const RandomPickSharePage = lazy(() => import('./pages/public/RandomPickSharePage'));
const AuthCallback = lazy(() => import('./pages/auth/AuthCallback'));
const SeoPage = lazy(() => import('./pages/public/SeoPage'));
const OnboardingFlow = lazy(() => import('./pages/onboarding/OnboardingFlow'));
const Dashboard = lazy(() => import('./pages/app/Dashboard'));
const CategoryView = lazy(() => import('./pages/app/CategoryView'));
const Favorites = lazy(() => import('./pages/app/Favorites'));
const Settings = lazy(() => import('./pages/app/Settings'));
const Billing = lazy(() => import('./pages/app/Billing'));
const Trash = lazy(() => import('./pages/app/Trash'));
const ProfilePage = lazy(() => import('./pages/public/ProfilePage'));
const SharedItemPage = lazy(() => import('./pages/public/SharedItemPage'));
const SharedFolderPage = lazy(() => import('./pages/public/SharedFolderPage'));
const ShareTargetPage = lazy(() => import('./pages/public/ShareTargetPage'));
const AdminPromos = lazy(() => import('./pages/app/AdminPromos'));
const SmartCollectionView = lazy(() => import('./pages/app/SmartCollectionView'));
const QuickSavePage = lazy(() => import('./pages/app/QuickSavePage'));
const ClipPage = lazy(() => import('./pages/app/ClipPage'));
const FamilyInvitePage = lazy(() => import('./pages/app/FamilyInvitePage'));
const JoinPromoPage = lazy(() => import('./pages/public/JoinPromoPage'));
const LandingPage = lazy(() => import('./pages/public/LandingPage'));
const JoinReferral = lazy(() => import('./pages/auth/JoinReferral'));
const LegalPage = lazy(() => import('./pages/public/LegalPage'));
const DeleteAccountPage = lazy(() => import('./pages/public/DeleteAccountPage'));
const CharterGateModal = lazy(() => import('./components/legal/CharterGateModal'));
const ShareHub = lazy(() => import('./pages/app/ShareHub'));
const PublicShareProfile = lazy(() => import('./pages/public/PublicShareProfile'));
const ProjectsHub = lazy(() => import('./pages/app/ProjectsHub'));
const ProjectDetail = lazy(() => import('./pages/app/ProjectDetail'));
const ProjectInvitationAccept = lazy(() => import('./pages/public/ProjectInvitationAccept'));
const Arcade = lazy(() => import('./pages/app/Arcade'));
const SurveysPage = lazy(() => import('./pages/app/SurveysPage'));
const PublicSurveyPage = lazy(() => import('./pages/public/PublicSurveyPage'));
const SecretSantaPage = lazy(() => import('./pages/app/SecretSantaPage'));
const SecretSantaJoinPage = lazy(() => import('./pages/public/SecretSantaJoinPage'));
const SecretSantaRevealPage = lazy(() => import('./pages/public/SecretSantaRevealPage'));
const ReceivedPage = lazy(() => import('./pages/app/ReceivedPage'));

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <img src="/logo-negatif.png"
          alt="TofBox" className="w-20 h-20 object-contain" />
        <div className="w-8 h-8 border-2 border-[#534AB7] border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  // Preserve the intended path + hash (e.g. `/settings#notifications`
  // when landing from an email footer) so the login flow can bring the
  // user right back where they wanted to go.
  if (!user) {
    const target = `${location.pathname}${location.search || ''}${location.hash || ''}`;
    const next = target && target !== '/' ? `?next=${encodeURIComponent(target)}` : '';
    return <Navigate to={`/login${next}`} replace />;
  }
  if (user && !user.onboarding_completed) {
    // Someone who signed up from a Secret Santa invitation goes straight to
    // the draw, from a shared folder or link to what they received, from a
    // project invitation to the project; the onboarding waits until they
    // open the rest.
    if (location.pathname === '/secret-santa' || location.pathname.startsWith('/received/')
      || /^\/projects\/[^/]+$/.test(location.pathname)) return children;
    const invite = pendingInvite();
    if (invite) return <Navigate to={invite} replace />;
    if (location.pathname !== '/dashboard') {
      rememberAfterSignup(`${location.pathname}${location.search || ''}${location.hash || ''}`);
    }
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function PublicLanding() {
  // The marketing landing page (hero, pricing, "install the app" pitch) is
  // pointless once someone is already inside the native app — they got
  // here by downloading it from the App Store. Skip straight to what they
  // actually need: the dashboard if logged in, the login screen otherwise.
  // Web keeps the full landing page for cold visitors, including logged-in
  // ones (contextual CTAs replace "Login / Sign Up" there instead).
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (Capacitor.isNativePlatform()) {
    return <Navigate to={user ? '/dashboard' : '/login'} replace />;
  }
  return <LandingPage />;
}

// The home page in one language (tof-box.app/fr…), for search engines and
// link previews; the copy the server sends for it is written by
// scripts/build-locale-pages.js. tof-box.app/ itself is English.
const LANDING_LANGS = ['fr', 'es', 'pt', 'nl', 'de'];

function LocalizedLanding({ lang }) {
  useEffect(() => {
    if (i18n.resolvedLanguage !== lang) i18n.changeLanguage(lang);
  }, [lang]);
  return <PublicLanding />;
}

// A Tof-Box link the installed app has no page for (a page added after this
// version was built) opens in the browser, where the site always has it,
// instead of ending on the login screen. The app goes back home behind it.
// Opening our own link could hand it straight back to the app, so the same
// link is never sent out twice in a row within a few seconds.
let lastSentOut = { url: null, at: 0 };

function UnknownPath() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const url = location.state?.fromLink;
    if (url && !(lastSentOut.url === url && Date.now() - lastSentOut.at < 10000)) {
      lastSentOut = { url, at: Date.now() };
      window.open(url, '_blank', 'noopener');
    }
    navigate('/', { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <AuthCallback />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/join" element={<JoinReferral />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/parking/:token" element={<ParkingSharePage />} />
        <Route path="/meet/:token" element={<TofMeetSharePage />} />
        <Route path="/pioche/:token" element={<ChoreWheelSharePage />} />
        <Route path="/countdown/:token" element={<CountdownSharePage />} />
        <Route path="/tirage/:token" element={<RandomPickSharePage />} />
        <Route path="/onboarding" element={<OnboardingFlow />} />
        <Route path="/u/:username" element={<PublicShareProfile />} />
        <Route path="/s/:token" element={<SharedItemPage />} />
        <Route path="/poll/:token" element={<PublicSurveyPage />} />
        <Route path="/santa/reveal/:revealToken" element={<SecretSantaRevealPage />} />
        <Route path="/santa/:token" element={<SecretSantaJoinPage />} />
        <Route path="/f/:token" element={<SharedFolderPage />} />
        <Route path="/share-target" element={<ShareTargetPage />} />
        <Route path="/projects/invite/:token" element={<ProjectInvitationAccept />} />
        <Route path="/family-invite/:token" element={<FamilyInvitePage />} />
        <Route path="/join/:code" element={<JoinPromoPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/legal/cgu" element={<LegalPage />} />
        <Route path="/legal/privacy" element={<LegalPage />} />
        <Route path="/legal/charte" element={<LegalPage />} />
        <Route path="/legal/cookies" element={<LegalPage />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />
        <Route path="/admin/promos" element={<ProtectedRoute><AdminPromos /></ProtectedRoute>} />
        <Route path="/smart/:id" element={<ProtectedRoute><SmartCollectionView /></ProtectedRoute>} />
        <Route path="/quick-save" element={<ProtectedRoute><QuickSavePage /></ProtectedRoute>} />
        <Route path="/clip" element={<ProtectedRoute><ClipPage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/category/:id" element={<ProtectedRoute><CategoryView /></ProtectedRoute>} />
        <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
        <Route path="/share" element={<ProtectedRoute><ShareHub /></ProtectedRoute>} />
        <Route path="/id" element={<ProtectedRoute><ShareHub /></ProtectedRoute>} />
        <Route path="/projects" element={<ProtectedRoute><ProjectsHub /></ProtectedRoute>} />
        <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
        <Route path="/trash" element={<ProtectedRoute><Trash /></ProtectedRoute>} />
        <Route path="/arcade" element={<ProtectedRoute><Arcade /></ProtectedRoute>} />
        <Route path="/surveys" element={<ProtectedRoute><SurveysPage /></ProtectedRoute>} />
        <Route path="/secret-santa" element={<ProtectedRoute><SecretSantaPage /></ProtectedRoute>} />
        <Route path="/received/:id" element={<ProtectedRoute><ReceivedPage /></ProtectedRoute>} />
        <Route path="/" element={<PublicLanding />} />
        {LANDING_LANGS.map((lang) => (
          <Route key={lang} path={`/${lang}`} element={<LocalizedLanding lang={lang} />} />
        ))}
        {SEO_PAGE_ROUTES.map(({ page, lang, path }) => (
          <Route key={path} path={path} element={<SeoPage page={page} lang={lang} />} />
        ))}
        <Route path="*" element={<UnknownPath />} />
      </Routes>
    </Suspense>
  );
}

// Pages don't scroll inside a height-clamped inner container (the
// `.app-container` flex column only sets a `min-height`), so the actual
// scrolling happens on the window — which is also why the swipe-back/
// forward gesture's own scroll restoration doesn't help: by the time it
// fires, React hasn't re-rendered the previous page's content yet, so the
// browser restores against the wrong document height and gives up at the
// top. Track each history entry's scroll position ourselves and replay it
// once the page has actually re-rendered.
const scrollPositionsByKey = new Map();

function ScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === 'POP') {
      const saved = scrollPositionsByKey.get(location.key);
      if (saved != null) requestAnimationFrame(() => window.scrollTo(0, saved));
    }
    const onScroll = () => scrollPositionsByKey.set(location.key, window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.key, navigationType]);

  return null;
}

function App() {
  useEffect(() => {
    // Let ScrollRestoration own this entirely — otherwise the browser's own
    // automatic restoration (which fires before React re-renders the target
    // page) fights with ours and usually loses, snapping to the top anyway.
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    // Bridge the ConsentBanner preferences with PostHog. PostHog is
    // opted-out by default in index.html; this hook opts in only when
    // the user explicitly accepts the "analytics" cookie category, and
    // re-syncs whenever the banner dispatches `tofbox_consent_changed`.
    initPosthogConsent();
    // Same bridge for Awin affiliate monetization — falls under the
    // "marketing" cookie category and stays fully out of the DOM until
    // explicit consent is given.
    initAwinConsent();
  }, []);

  return (
    // reducedMotion="user": with the phone's "reduce motion" setting on,
    // every Framer Motion animation drops its movement (fades only).
    <MotionConfig reducedMotion="user">
      <AuthProvider>
        <VaultProvider>
          <BrowserRouter>
            <AppRouter />
            <ScrollRestoration />
            <ConsentBanner />
            <UpdateBanner />
            <CharterGate />
            <DeepLinkHandler />
            <Toaster position="top-center" theme="dark" richColors closeButton />
          </BrowserRouter>
        </VaultProvider>
      </AuthProvider>
    </MotionConfig>
  );
}

function extractUrlFromText(str) {
  if (!str) return null;
  const match = str.match(/https?:\/\/[^\s]+/);
  return match ? match[0] : null;
}

/** Checks the App Group for links stashed by the native iOS Share Extension
 *  (ios/App/ShareExtension) and, if any are found, hands them off exactly
 *  like the web Share Target flow does (sessionStorage + "/dashboard?share=1"
 *  — see ShareTargetPage.jsx) so the Save modal opens prefilled and the user
 *  picks the folder themselves — then reopens for the next queued link, if
 *  any (see the shareQueue handling in Dashboard.jsx). Share Extensions run
 *  as a separate process and Apple does not let them re-open their
 *  containing app, so there is no reliable "auto open" signal to hook —
 *  this has to be polled whenever the app becomes visible, which is also
 *  why it's safe to call unconditionally (it resolves to nothing unless the
 *  user just came back from the share sheet, in which case the native side
 *  has already cleared it after this one read). No-op on web/Android. */
function checkPendingShare(navigate) {
  if (!Capacitor.isNativePlatform()) return;
  TofBoxShare.getPendingShares()
    .then(({ items }) => {
      const queued = (items || [])
        .map(({ url, text, title }) => ({
          title: title || '',
          text: text || '',
          url: url || extractUrlFromText(text) || '',
        }))
        .filter((i) => i.url || i.text);
      if (queued.length === 0) return;
      try {
        sessionStorage.setItem('tofbox_share_target', JSON.stringify(queued[0]));
        if (queued.length > 1) {
          sessionStorage.setItem('tofbox_share_queue', JSON.stringify(queued.slice(1)));
        }
      } catch { /* noop */ }
      navigate('/dashboard?share=1');
    })
    .catch(() => {});
}

/** Universal Links (native only): a tof-box.app link tapped from WhatsApp,
 *  Mail, SMS, etc. opens straight into the app instead of Safari when the
 *  app is installed — iOS still hands the URL to us via this event rather
 *  than a normal page load, so we route it ourselves. Requires the
 *  Associated Domains capability + apple-app-site-association file; inert
 *  no-op on web. Also picks up pending Share Extension handoffs — see
 *  checkPendingShare above. */
function DeepLinkHandler() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    // Cold launch (app wasn't running before the share) and coming back to
    // the foreground (app was just backgrounded while the user was in the
    // share sheet) are both ways the user can land back in Tof-Box after
    // using the Share Extension — check both.
    checkPendingShare(navigate);
    const urlListenerPromise = CapacitorApp.addListener('appUrlOpen', ({ url }) => {
      // Only Tof-Box's own links lead somewhere in the app. Any website or
      // app can open the custom scheme (tofboxshare://), so its path is
      // never followed — the app just opens.
      try {
        const { protocol, hostname, pathname, search, hash } = new URL(url);
        if (protocol === 'https:' && /^(www\.)?tof-box\.app$/i.test(hostname)) {
          // A link given out through the link preview (/api/p/…) opens its page.
          // public/.well-known/apple-app-site-association leaves /api/* out on
          // purpose: app versions without this line sent such a link to the
          // home page, so it opens in the browser instead and its preview
          // page leads to the page.
          // The link itself rides along: a page this version doesn't have
          // yet opens in the browser (see UnknownPath).
          navigate(sharedLinkPage(pathname) || `${pathname}${search}${hash}`, { state: { fromLink: url } });
        }
      } catch { /* malformed URL — ignore */ }
    });
    const stateListenerPromise = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) checkPendingShare(navigate);
    });
    return () => {
      urlListenerPromise.then((sub) => sub.remove());
      stateListenerPromise.then((sub) => sub.remove());
    };
  }, [navigate]);
  return null;
}

/** Shows the acceptable-use charter as a blocking modal for any signed-in
 *  user who has not yet accepted it (Google signups or legacy accounts
 *  created before the charter was introduced). Public routes are exempt. */
function CharterGate() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || !user) return null;
  if (user.charter_accepted_at) return null;
  // Public routes and the onboarding funnel are exempt — the gate is meant
  // for accounts about to interact with real user content.
  const path = location.pathname || '/';
  const exemptPrefixes = ['/legal', '/login', '/register', '/join', '/forgot-password',
    '/reset-password', '/onboarding', '/family-invite', '/projects/invite', '/s/', '/f/',
    '/u/', '/parking/', '/meet/', '/pioche/', '/santa/', '/countdown/', '/tirage/', '/share-target', '/quick-save'];
  if (path === '/' || exemptPrefixes.some((p) => path.startsWith(p))) return null;
  return (
    <Suspense fallback={null}>
      <CharterGateModal />
    </Suspense>
  );
}

export default App;
